// =============================================================================
// FILE: packages/renderer/src/stores/gatekeeperAlert.store.ts
// PURPOSE: Real-time Founder's Gatekeeper alerting. Tracks blocked-domain
//          login attempts and exposes an "unseen" count for the Global Command
//          Center sidebar badge, plus the newest attempt to surface as a toast.
//          The "last seen" watermark persists in localStorage so historical
//          attempts are never re-alerted after a reload.
// =============================================================================

import { create } from 'zustand';

export interface GkAttempt {
  id: string;
  email: string;
  domain: string;
  match_type: string;
  match_value: string;
  ip: string;
  created_at: string;
}

const LS_KEY = 'rcd2.gk.lastSeen';

function initLastSeen(): string {
  try {
    const stored = localStorage.getItem(LS_KEY);
    if (stored) return stored;
    // First run — treat everything that exists now as already seen.
    const now = new Date().toISOString();
    localStorage.setItem(LS_KEY, now);
    return now;
  } catch {
    return new Date().toISOString();
  }
}

interface GatekeeperAlertState {
  lastSeenAt: string;
  unseenCount: number;
  toast: GkAttempt | null;
  lastToastId: string | null;
  ingest: (rows: GkAttempt[]) => void;
  markAllSeen: () => void;
  dismissToast: () => void;
}

export const useGatekeeperAlertStore = create<GatekeeperAlertState>((set, get) => ({
  lastSeenAt: initLastSeen(),
  unseenCount: 0,
  toast: null,
  lastToastId: null,

  ingest: (rows) => {
    const { lastSeenAt, lastToastId } = get();
    const unseen = rows.filter((r) => r.created_at > lastSeenAt);
    const newest = unseen[0] ?? null; // registry is sorted newest-first
    const shouldToast = newest && newest.id !== lastToastId;
    set({
      unseenCount: unseen.length,
      ...(shouldToast ? { toast: newest, lastToastId: newest.id } : {}),
    });
  },

  markAllSeen: () => {
    const now = new Date().toISOString();
    try { localStorage.setItem(LS_KEY, now); } catch { /* */ }
    set({ lastSeenAt: now, unseenCount: 0 });
  },

  dismissToast: () => set({ toast: null }),
}));
